import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  authService,
  roles,
  storage,
  validators,
} from '@innovamat/radiance-utils';
import type { LoginResponse } from '@innovamat/social-login';
import { useAuthState } from '@innovamat/social-login';
import { useWebviewBridge } from '@innovamat/webview-bridge';

import { useEventLogging } from '@innovamat/event-logging';
import { useFlag } from '@innovamat/flags';
import { useQueryParams } from '@innovamat/hooks';
import { useEnvVariables } from '../../env-variables-manager';
import { useUser } from '../../user-management';
import type { NotificationType } from '../types/notification-type';

function useLogin(studentJuniorLimitationEnabled = true, isWebApp?: boolean) {
  const { WEBAPP, MANAGER_NEXT } = useEnvVariables();
  const { t } = useTranslation();
  const [isUSA, setIsUSA] = useState(false);
  const { setEventData } = useEventLogging();
  const { onLogin, loading, error, loginResponse } = useAuthState();
  const { isInWebview, isGreenApp } = useWebviewBridge();
  const [showWebViewModal, setShowWebViewModal] = useState(false);
  const { onSetUserLogin, onClearUserSession } = useUser();

  const getIsLoading = () => loading && Object.values(loading).some(Boolean);
  const isLoading = getIsLoading();

  const [showPassword, setShowPassword] = useState(false);
  const [displayError, setDisplayError] = useState(false);
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
  });

  const [showEmailError, setShowEmailError] = useState(false);

  const navigate = useNavigate();
  const referral = useQueryParams().get('referral');

  const { value: showCleverLoginButton } = useFlag(
    'showCleverLoginButton',
    false
  );

  const { value: showClassLinkLoginButton } = useFlag(
    'showClassLinkLoginButton',
    false
  );

  useEffect(() => {
    const retryInterval = 100;
    const maxRetries = 50;
    let retries = 0;

    const checkAndLogin = (): void => {
      if (isInWebview && window.uniWebViewAccessToken) {
        onLogin({
          type: 'login_from_webview',
          payload: window.uniWebViewAccessToken,
        });
      } else if (retries < maxRetries) {
        setTimeout(checkAndLogin, retryInterval);
        retries++;
      }
    };

    checkAndLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInWebview]);

  const [searchParams, setSearchParams] = useSearchParams();

  const code = searchParams.get('code');
  const dc = searchParams.get('dc');

  useEffect(() => {
    if (!loginResponse) return;

    if (code && dc) {
      onClearUserSession();
      setSearchParams(
        (prev: URLSearchParams) => {
          prev.delete('code');
          prev.delete('dc');
          return prev;
        },
        { replace: true }
      );
    }

    // Si es junior.
    if (studentJuniorLimitationEnabled && getIsStudentJunior(loginResponse)) {
      storage.tokenInfo.set(loginResponse);
      if (isInWebview) {
        setShowWebViewModal(true);
      } else {
        searchParams.delete('code');
        const urlWithParams = `${WEBAPP}?${searchParams.toString()}`;
        window.location.href = urlWithParams;
        return;
      }
      // Si no es junior.
    } else {
      onSetUserLogin(loginResponse);

      if (isInWebview && isGreenApp && getIsStudent(loginResponse)) {
        navigate('/practice/home', { replace: true });
      }

      if (isWebApp && !isInWebview && getIsStudent(loginResponse)) {
        storage.clear();
        window.location.href = MANAGER_NEXT;
      }

      if (referral) {
        navigate(referral, { replace: true });
      } else {
        navigate('/', { replace: true });
      }

      return;
    }
  }, [loginResponse, isInWebview, isGreenApp, studentJuniorLimitationEnabled]);

  const handleCheckEmail = (email: string): void => {
    if (email) {
      setShowEmailError(true);
    }
  };

  const handleHideWebViewModal = () => {
    setShowWebViewModal(false);
  };

  const getIsStudentJunior = (tokenData: LoginResponse) => {
    const user = authService.decodeToken(tokenData);
    return roles.hasStudentJuniorRole(user?.roles);
  };

  const getIsStudent = (tokenData: LoginResponse) => {
    const user = authService.decodeToken(tokenData);
    return roles.hasStudentRole(user?.roles);
  };

  const isUserDisabled = error?.title === 'user.is_disabled';
  const hasExistingAccount = window.location.search.includes(
    'warning=already_registered'
  );

  useEffect(() => {
    setDisplayError(!!error);
    if (error && credentials.username) {
      setEventData(
        'login_attempt_failed',
        {
          username: credentials.username,
        },
        false
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const handleSetCredentials = (value: string, name: string) => {
    setCredentials((prevVal) => ({
      ...prevVal,
      [name]: value,
    }));
  };

  const getNotification = (): NotificationType => {
    if (hasExistingAccount) {
      return {
        message: t(
          'Login.Esta dirección de correo electrónico ya tiene una cuenta activa asignada. Ingresa con ella para acceder al Gestor de aula.',
          'El email que has puesto ya tiene una cuenta. Accede a tu cuenta en esta página.'
        ),
        type: 'warning',
      };
    }

    if (!displayError) return undefined;

    if (isUserDisabled) {
      return {
        message: t(
          'Login.messageError.user.is_disabled',
          'Tu cuenta ha sido desactivada por tu escuela. Habla con ellos para poder acceder.'
        ),
        type: 'error',
      };
    }

    return {
      message: (
        <Trans
          i18nKey="Login.messageError.incorrectUser"
          components={{
            b: <b />,
            a: (
              <a
                href={t('common.accountProblems.url')}
                target="_blank"
                rel="noreferrer"
              />
            ),
          }}
        />
      ),
      type: 'error',
    };
  };

  const notification = getNotification();

  const handleDisplayError = (value: boolean) => {
    setDisplayError(value);
  };

  const handleToggleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const isValid = (): boolean => {
    const { username, password } = credentials;

    if (!username.trim() || !password.trim()) return false;
    if (!validators.isEmailValid(username)) return false;

    return true;
  };

  const handleIsUsa = (value: boolean) => {
    setIsUSA(value);
  };

  const showCleverBtn = isUSA && showCleverLoginButton;
  const showClassLinkBtn = isUSA && showClassLinkLoginButton;
  return {
    credentials,
    displayError,
    error,
    isLoading,
    notification,
    showEmailError,
    showPassword,
    showWebViewModal,
    handleCheckEmail,
    handleDisplayError,
    handleHideWebViewModal,
    handleSetCredentials,
    handleToggleShowPassword,
    isValid,
    onLogin,
    handleIsUsa,
    showCleverBtn,
    showClassLinkBtn,
  };
}

export { useLogin };
